import { useCallback, useRef } from 'react';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { domainForSegment } from '../utils/Common';

/**
 * Default analytics segment: current domain + current path.
 *
 * Resolved LAZILY (called when an event fires), never captured at render time.
 * This guarantees the segment reflects the URL at the moment of the user
 * interaction — critical for components that stay mounted across route changes
 * (e.g. header, hamburger menu, idle-timeout, language dropdown), which would
 * otherwise report a stale path.
 */
const defaultAnalyticsSegment = () =>
  `${domainForSegment}${globalThis.location.pathname}`;

/** Default Adobe content-listing event type. */
const DEFAULT_CONTENT_LISTING_EVENT_TYPE = 'Content Listing Item Clicked';

/**
 * Optional overrides for a content-listing event.
 *
 * - `eventType`: overrides the default `'Content Listing Item Clicked'` string.
 *   Use this ONLY when analytics requires a domain-specific event type for a
 *   given UI element (e.g. dashboard tiles emit the tile name, claims emit the
 *   action title). Most calls should OMIT this so the default is applied
 *   consistently across the app.
 * - `extraFields`: additional properties merged into the single content item
 *   (e.g. `previousLanguage`/`newLanguage`, `interactionSource`, `cardType`,
 *   `planType`). Intentionally loosely typed (`Record<string, …>`) because
 *   Adobe's `Content` type has no index signature, yet accepts arbitrary
 *   properties at runtime; keep keys aligned with the analytics spec.
 */
export type ContentListingOptions = {
  eventType?: string;
  extraFields?: Record<string, string | number | undefined>;
};

/**
 * Pure builder for the Adobe "Content Listing Item Clicked" event payload.
 * Kept separate from the hook so it can be unit-tested and reused without React.
 */
export const buildContentListingEvent = (
  segment: string,
  contentId: string,
  contentTitle: string,
  itemPosition: number,
  {
    eventType = DEFAULT_CONTENT_LISTING_EVENT_TYPE,
    extraFields,
  }: ContentListingOptions = {}
) => ({
  segment,
  contentListing: {
    eventType,
    listing: {
      content: [
        { contentId, contentTitle, itemPosition, ...extraFields },
      ],
    },
  },
});

/**
 * Single analytics facade for the app.
 *
 * This is the ONLY place that should send Adobe content-listing events
 * (`contentListingItemClicked`). Other Adobe events (pageView, form events)
 * are still called directly via `useAdobeAnalytics` and can be added here as
 * new `trackXxx` methods that reuse `defaultSegment`.
 *
 * The analytics segment defaults to `${domainForSegment}${pathname}` resolved
 * at the moment each event fires. It can be overridden at two levels:
 *   1. Per hook instance, via `defaultSegment` — use ONLY when a component must
 *      report a FIXED segment regardless of the current URL.
 *   2. Per call, via the `segment` argument to `trackContentListing`.
 *
 * Precedence when an event fires: per-call `segment` → hook `defaultSegment` →
 * live `defaultAnalyticsSegment()`.
 *
 * @param defaultSegment Optional fixed segment for calls that don't pass their
 *   own. When omitted, the live current-path segment is used at call time.
 */
export const useAnalytics = (defaultSegment?: string) => {
  const { contentListingItemClicked } = useAdobeAnalytics();

  /**
   * Hold the latest `defaultSegment` in a ref so `trackContentListing` can read
   * the current value without listing it as a dependency. This keeps the
   * callback referentially stable across renders (safe to pass to memoized
   * children / effect deps) while never serving a stale segment.
   *
   * MAINTAINER NOTE: intentionally do NOT add this to the `useCallback` deps
   * below — reading it via a ref is what prevents the render-time stale-closure
   * bug this hook was written to fix.
   */
  const defaultSegmentRef = useRef(defaultSegment);
  defaultSegmentRef.current = defaultSegment;

  const trackContentListing = useCallback(
    (
      contentId: string,
      contentTitle: string,
      itemPosition: number,
      segment?: string,
      options?: ContentListingOptions
    ) => {
      // Precedence: per-call `segment` → hook `defaultSegment` → live
      // current-path segment.
      // Use `||` (not `??`) so an empty-string segment also falls back: an
      // empty segment is never valid in the analytics schema.
      const resolvedSegment =
        segment || defaultSegmentRef.current || defaultAnalyticsSegment();
      contentListingItemClicked(
        buildContentListingEvent(
          resolvedSegment,
          contentId,
          contentTitle,
          itemPosition,
          options
        )
      );
    },
    [contentListingItemClicked]
  );

  return { trackContentListing };
};
